import {
  deeplinkCanonicalPath,
  getUserPref,
  parseDeeplink,
  readOrgCode,
  setUserOrgPref,
  setUserPref,
  spErrorTags,
  withOrgCode,
} from "@eventr/common";
import { requestsEventrSharedNoAuth } from "data/api";
import { EventrGatewayAPI } from "data/gateway.api";
import { useRegStore } from "feat/authentication/registrationProvider";
import { logEvent } from "feat/firebase/firebase";
import {
  clearGuestMode,
  isGuestStoreRehydrated,
  useGuestStore,
  whenGuestStoreRehydrated,
} from "feat/guest/guest.store";
import { setPendingGuestLink } from "feat/guest/pendingGuestLink";
import { resetToTabRoot } from "feat/nativeNavigation/NativeRootController";
import { isIosNativeNavigation } from "feat/nativeNavigation/platform";
import { getEmailFromTokenResponse } from "feat/oauth/interfaces";
import i18n from "i18n/i18next";
import { routes } from "routing";
import type { RouteNode } from "routing/route";
import { bs, useBoundStore } from "state/bound.store";
import { defaultAddSnack } from "state/overlayState/overlayContext";
import { useStorageStore } from "state/storage/storage.store";
import { clearPendingAuthLink, pendingAuthLink, setPendingAuthLink } from "./auth-deeplink";
import { lookupAuthRoute } from "./legacy-auth-path";
import { markOpenedUrlSpent } from "./nativeLaunchUrl";

/** A deeplink waiting for the app to be ready enough to resolve it. */
interface PendingDeeplink {
  /** The in-app path to navigate to, with the org parameter already taken off. */
  path: string;
  /** The org the link belongs to, if it carried one. */
  orgCode?: string;
  /** Whether the profile has already been refetched while checking membership. */
  profileRefreshed?: boolean;
}

// The app's URL and notification listeners are registered at module load, so a launch deeplink can
// arrive long before the app can act on it: before GlobalRouter has registered `window.pushPath`,
// before there is a session, and before the profile that says which organisations the user belongs
// to has loaded. The link is stashed here and applied once every gate it needs is open.
let pending: PendingDeeplink | null = null;

// Set while an organisation switch is in flight. The switch awaits ~8 fetches, during which the
// store changes repeatedly and the subscription below fires; without this the resolver would
// re-enter and start a second switch on top of the first.
let switching = false;

let launchLocationIngested = false;

// Resolvers awaiting a round-trip of their own. Between a link arriving and the screen it
// resolves to, it is in neither `pending` nor the pending auth link, so nothing else names this
// window.
let resolving = 0;

/** Whether a link is still being resolved, stashed, or held open on the pre-auth screens. */
export function isDeeplinkInFlight(): boolean {
  return resolving > 0 || pending !== null || pendingAuthLink() !== null;
}

/** Counts `work` as a resolution in flight for as long as it runs. */
function track(work: Promise<void>): void {
  resolving += 1;
  void work.finally(() => {
    resolving -= 1;
  });
}

/** Navigate to an in-app deeplink now if the app is ready, otherwise stash it until it is. */
export function navigateToDeeplink(target: string): void {
  // A guest link takes over the screen, the same way an auth link does.
  if (new URL(target, "http://eventr.invalid").pathname.startsWith("/guest/")) {
    setPendingGuestLink(target);
    return;
  }

  // Guest mode is persisted, so until it has read back a launch link cannot tell whether it is
  // opening over a guest view.
  if (isGuestStoreRehydrated() && !useGuestStore.getState().isGuestMode) {
    routeDeeplink(target);
    return;
  }
  track(leaveGuestModeForSession().then(() => routeDeeplink(target)));
}

/**
 * Ends a guest view opened over a signed-in session and reselects the account's own team, so a
 * link or notification resolves against the account rather than the public itinerary. A guest
 * with no session stays a guest.
 */
async function leaveGuestModeForSession(): Promise<void> {
  await Promise.all([waitForHydration(), whenGuestStoreRehydrated()]);
  if (!useGuestStore.getState().isGuestMode || !bs().IDtoken) return;
  // As the swap-team page does, so setSelectedOrg stops treating the session as a guest.
  useGuestStore.setState(useGuestStore.getInitialState());
  const organisations = bs().profile?.organisations ?? [];
  const defaultOrg = getUserPref("account", "defaultOrg");
  const org = organisations.find((candidate) => candidate.name === defaultOrg) ?? organisations[0];
  if (!org) return;
  try {
    await bs().setSelectedOrgById(org.name);
  } catch (error) {
    spErrorTags("Failed to leave guest mode for a deeplink", ["DEEPLINK"], error);
  }
}

/** Routes a deeplink once any guest view over a session has been left. */
function routeDeeplink(target: string): void {
  const link = parseDeeplink(target);
  if (link) {
    // Canonicalised here, query string included, so no client-side `/item/*` redirect is needed.
    pending = { path: deeplinkCanonicalPath(link), orgCode: link.orgCode };
    void applyPendingDeeplink();
    return;
  }

  const authRoute = lookupAuthRoute(target);
  if (authRoute) {
    rememberLinkedItinerary(target);
    // The callback carries an opaque code, so the account is only known after the exchange.
    if (authRoute.path === routes.oauthCallback.path) {
      track(resolveOAuthCallback(target));
      return;
    }
    // An invite link carries a token. A sign-up-details link without one is the plain
    // create-account flow.
    if (authRoute.path === routes.auth.signUpDetails.path) {
      const token = readQueryParam(target, "token");
      const org = readQueryParam(target, "org");
      if (token && org) {
        track(resolveInviteLink(token, org));
        return;
      }
    }
    // Sign-in, forgot-password and confirm-email links carry the account's address. A link
    // without one has nothing to check against and is pushed as given.
    const forAccount = readQueryParam(target, "email");
    if (!forAccount) {
      setPendingAuthLink(authRoute, target);
      return;
    }
    track(resolveAddressLink(authRoute, target, forAccount));
    return;
  }

  // Any other path is somebody else's route, not an error. Navigate it as given.
  pending = { path: target };
  void applyPendingDeeplink();
}

/**
 * Reads one query-string value from a path+query target. Avoids `URLSearchParams`, which
 * decodes a literal `+` to a space and so corrupts an address containing one.
 */
function readQueryParam(target: string, key: string): string | undefined {
  const { search } = new URL(target, "http://eventr.invalid");
  for (const pair of search.slice(1).split("&")) {
    const eq = pair.indexOf("=");
    const rawKey = eq === -1 ? pair : pair.slice(0, eq);
    if (rawKey !== key) continue;
    return eq === -1 ? "" : decodeURIComponent(pair.slice(eq + 1));
  }
  return undefined;
}

/** The org an emailed link names, and the itinerary in it when it names one. */
interface LinkedItinerary {
  org: string;
  itineraryId?: number;
}

/**
 * The first `org` is the team. The access-approved email appends the itinerary as a second
 * `org` rather than as `itin`, so that link names a team and no itinerary.
 */
function readLinkedItinerary(target: string): LinkedItinerary | undefined {
  const org = readQueryParam(target, "org");
  if (!org) return undefined;
  const itineraryId = Number(readQueryParam(target, "itin"));
  return Number.isInteger(itineraryId) && itineraryId > 0 ? { org, itineraryId } : { org };
}

/**
 * Makes the itinerary an invite link names its org's default, so the first itinerary shown after
 * sign-in, sign-up or joining is the one the invitee was added to. AuthLayout does the same from
 * the router's query string, but a native link is resolved here and may land on a screen whose
 * path no longer carries it.
 */
function rememberLinkedItinerary(target: string): void {
  const linked = readLinkedItinerary(target);
  if (linked?.itineraryId === undefined) return;
  setUserOrgPref("itinerary", "defaultItinerary", linked.itineraryId.toString(), linked.org);
}

/**
 * Shows the team, and the itinerary when there is one, that an emailed link names to an invitee
 * who opened it already signed in. The preference alone is not enough here: an itinerary is
 * already selected, so nothing re-reads it.
 */
async function openLinkedItinerary(target: string): Promise<void> {
  const linked = readLinkedItinerary(target);
  if (!linked) return;
  const { itineraryId } = linked;
  try {
    if (bs().selectedOrg?.name !== linked.org) {
      // An access-approved email arrives for a team the cached profile predates, and the
      // foreground refetch in onReturn lands seconds after this, so refetch once here.
      const isMember = () => bs().profile?.organisations?.some((org) => org.name === linked.org) ?? false;
      if (!isMember()) await bs().getProfile();
      if (!isMember()) return;
      // Moves with the switch, as in applyPendingDeeplink, or onReturn switches straight back.
      setUserPref("account", "defaultOrg", linked.org);
      // Loads the org's itinerary map, and the selection it makes reads the preference.
      await bs().setSelectedOrgById(linked.org);
      return;
    }
    if (itineraryId === undefined) return;
    // The invite added this account moments ago, so the cached map can predate it.
    const map = await bs().forceItineraryMapUpdate();
    if (map.some((itinerary) => itinerary.id === itineraryId)) {
      await bs().setCurrentItin(itineraryId);
    }
  } catch (error) {
    spErrorTags("Failed to open the itinerary an invite link names", ["DEEPLINK"], error);
  }
}

/** Resolves once the bound store has rehydrated from IndexedDB, immediately if it already has. */
function waitForHydration(): Promise<void> {
  if (useStorageStore.getState().hasHydrated === "finished") return Promise.resolve();
  return new Promise((resolve) => {
    const unsubscribe = useStorageStore.subscribe((state) => {
      if (state.hasHydrated !== "finished") return;
      unsubscribe();
      resolve();
    });
  });
}

/** Waits for hydration, then reads the identity gates every resolver below checks. */
async function readIdentityGates() {
  await waitForHydration();
  const { IDtoken, wpUser } = bs();
  const isGuest = useGuestStore.getState().isGuestMode;
  return { IDtoken, wpUser, isGuest };
}

/**
 * Resolves an oauth-callback link against whoever is signed in. Signed out or guest commits the
 * exchanged tokens, the same account refreshes the session in place, a different account holds
 * the tokens for the mismatch screen.
 */
async function resolveOAuthCallback(target: string): Promise<void> {
  const code = new URL(target, "http://eventr.invalid").searchParams.get("code") ?? "";
  const resp = await bs().exchangeCode(code);
  if (resp.status !== "success" || !resp.data) {
    defaultAddSnack(i18n.t("snack.auth-link-failed"), "error");
    return;
  }
  const tokens = resp.data;
  const forAccount = getEmailFromTokenResponse(tokens);

  // The code is single-use, so it is exchanged first. Only the identity decision waits.
  const { IDtoken, wpUser, isGuest } = await readIdentityGates();

  if (!IDtoken && !isGuest) {
    bs().commitTokens(tokens);
    await bs().onLogin();
    return;
  }

  if (isGuest) {
    bs().commitTokens(tokens);
    await bs().onLogin();
    clearGuestMode();
    return;
  }

  const signedInAccount = wpUser?.email;
  if (signedInAccount && signedInAccount.toLowerCase() === forAccount.toLowerCase()) {
    bs().commitTokens(tokens);
    if (!(await resetToTabRoot("home"))) window.pushPath(routes.home.path);
    return;
  }

  setPendingAuthLink(routes.auth.notSameUser, routes.auth.notSameUser.path, {
    forAccount,
    signedInAccount: signedInAccount ?? "",
    tokens,
  });
}

/**
 * Resolves a sign-in, forgot-password or confirm-email link against whoever is signed in. Signed
 * out or guest proceeds to the link's own screen, the same account lands on Home, a different
 * account sees the mismatch screen.
 */
async function resolveAddressLink(route: RouteNode, target: string, forAccount: string): Promise<void> {
  const { IDtoken, wpUser, isGuest } = await readIdentityGates();

  if (!IDtoken || isGuest) {
    setPendingAuthLink(route, target);
    return;
  }

  const signedInAccount = wpUser?.email;
  if (signedInAccount && signedInAccount.toLowerCase() === forAccount.toLowerCase()) {
    await openLinkedItinerary(target);
    if (!(await resetToTabRoot("home"))) window.pushPath(routes.home.path);
    return;
  }

  setPendingAuthLink(routes.auth.notSameUser, routes.auth.notSameUser.path, {
    forAccount,
    signedInAccount: signedInAccount ?? "",
  });
}

/**
 * Resolves an invite link against whoever is signed in. Signed out, guest, or a matching account
 * redeems the invite, a different account sees the mismatch screen with the token held for it.
 */
async function resolveInviteLink(token: string, org: string): Promise<void> {
  const { IDtoken, wpUser, isGuest } = await readIdentityGates();

  if (IDtoken && !isGuest) {
    // Read-only, unlike the redeem below, which joins the account to the org.
    const lookup = await requestsEventrSharedNoAuth.post("auth/tokenFromEmail", { token });
    if (lookup.status !== "success" || !lookup.data) {
      defaultAddSnack(i18n.t("snack.auth-link-failed"), "error");
      return;
    }
    const forAccount: string = lookup.data.user_email;
    logEvent("sign_up_invited_flow_started", { email: forAccount });
    if (wpUser?.email && wpUser.email.toLowerCase() !== forAccount.toLowerCase()) {
      // originKind lets the mismatch screen tell an invite mismatch apart from other kinds.
      setPendingAuthLink(routes.auth.notSameUser, routes.auth.notSameUser.path, {
        forAccount,
        signedInAccount: wpUser.email,
        inviteToken: token,
        inviteOrg: org,
        originKind: routes.auth.signUpDetails.path,
      });
      return;
    }
  }

  await confirmInviteAndLand(token, org);
}

/**
 * Redeems an invite token against its org, joining the account to it. Lands on the details form
 * for a new user, or the joined confirmation for one who already has an account. On failure a
 * signed-out session lands on the sign-in form, and any other session stays where it is.
 */
async function confirmInviteAndLand(token: string, org: string): Promise<void> {
  const resp = await EventrGatewayAPI.registration.confirmInvite(org, token);
  if (resp.status === "error" || !resp.data) {
    const usedUp = resp.message === "PS - Org already added";
    // A token the server has already spent stays spent, so a replay would only repeat this.
    // Any other failure leaves the link openable again, since it may never have been read.
    if (usedUp) markOpenedUrlSpent();
    defaultAddSnack(i18n.t(usedUp ? "snack.link-been-used" : "snack.failed-confirm-invite"), "error");
    const { IDtoken } = bs();
    if (!IDtoken && !useGuestStore.getState().isGuestMode) {
      setPendingAuthLink(routes.auth.signIn, routes.auth.signIn.path);
    }
    return;
  }

  // Redeemed: the token is gone, so a reload from here must not open the link again.
  markOpenedUrlSpent();

  if (resp.data.status === "exist") {
    if (resp.data.email) {
      // A session here means the invitee opened the link signed in as the invited account; the
      // signed-out and mismatch-continue paths into this branch carry none.
      const { IDtoken, isGuest } = await readIdentityGates();
      useRegStore.getState().setInviteData({
        email: resp.data.email.toLowerCase(),
        org,
        notSameUser: IDtoken && !isGuest ? false : undefined,
      });
    }
    setPendingAuthLink(routes.auth.joined, routes.auth.joined.path);
    return;
  }

  const invite = resp.data.tokenFromEmail;
  useRegStore.getState().setInviteData({
    email: invite.user_email.toLowerCase(),
    wp_id: invite.wp_id,
    org,
    flow_type: "invite",
  });
  // The details form reads the token off the path to decide whether to offer social sign-up.
  setPendingAuthLink(
    routes.auth.signUpDetails,
    `${routes.auth.signUpDetails.path}?token=${encodeURIComponent(token)}&org=${encodeURIComponent(org)}`,
  );
}

/**
 * Continues past a mismatch, signing out the account that was signed in. Held tokens sign in the
 * link's account, a held invite token redeems the invite, an address alone reopens the sign-in
 * form at that address.
 */
export async function continuePendingAuthLink(): Promise<void> {
  const link = pendingAuthLink();
  if (link?.tokens) {
    await bs().logout();
    bs().commitTokens(link.tokens);
    clearPendingAuthLink();
    await bs().onLogin();
    return;
  }
  if (link?.inviteToken) {
    await bs().logout();
    await confirmInviteAndLand(link.inviteToken, link.inviteOrg ?? "");
    return;
  }
  if (link) {
    // Set before signing out, so needsPush reads a stable presentedNativeRoot() === "auth".
    const email = encodeURIComponent(link.forAccount ?? "");
    setPendingAuthLink(routes.auth.signIn, `${routes.auth.signIn.path}?email=${email}`);
    await bs().logout();
  }
}

/** Cancels a mismatch. Drops the held tokens and leaves the session untouched. */
export function cancelPendingAuthLink(): void {
  clearPendingAuthLink();
  // Off iOS the mismatch screen was reached by a push, so it is popped. On iOS clearing the
  // link reconciles the root back to the tabs.
  if (!isIosNativeNavigation()) window.goBack();
}

/** Applies any stashed deeplink. Called by GlobalRouter once it has registered `window.pushPath`. */
export function flushPendingDeeplink(): void {
  void applyPendingDeeplink();
}

/**
 * Feeds the URL the app was loaded at through the resolver, when it carries an organisation.
 *
 * `appUrlOpen` only fires on native, so on the web a universal link is not an event at all: the
 * browser loads the SPA straight at the link's path and react-router renders it, with nobody
 * having looked at the org it names. This is the web's equivalent of that listener.
 *
 * Deliberately narrow. It does nothing unless the launch URL actually carries the org parameter,
 * so an ordinary page load — and every native launch, where the webview's own location carries no
 * link at all — is untouched.
 *
 * One-shot per app session: a newly displayed screen must not re-open the launch location as if
 * a fresh emailed link had arrived.
 */
export function ingestLaunchLocation(): void {
  if (launchLocationIngested) return;
  launchLocationIngested = true;
  const { search, pathname, hash } = window.location;
  if (!readOrgCode(search)) return;
  navigateToDeeplink(`${pathname}${search}${hash}`);
}

/** The screen shown for a link into an organisation the user cannot open. */
function noAccessPath(orgCode: string): string {
  return withOrgCode(routes.link.noAccess.path, orgCode);
}

/** Navigates to a resolved deeplink, unless a newer one has arrived in the meantime. */
function navigate(link: PendingDeeplink, path: string): void {
  if (pending !== link) {
    void applyPendingDeeplink();
    return;
  }
  pending = null;
  window.pushPath(path);
}

/**
 * Resolves the stashed deeplink as far as the app's current state allows.
 *
 * Re-entrant and safe to call at any time: it returns without doing anything while a gate it needs
 * is still shut, and the store subscription at the bottom of this module calls it again each time
 * one of those gates might have opened (the router mounting, a sign-in completing, the profile
 * arriving, an organisation switch finishing).
 */
export async function applyPendingDeeplink(): Promise<void> {
  const link = pending;
  if (!link || switching) return;

  // The router has to exist before anything can be navigated to.
  if (typeof window.pushPath !== "function") return;

  // A link with no organisation resolves against whichever one is selected, exactly as every link
  // did before the org schema. Nothing to check, so it goes as soon as the router is up.
  if (!link.orgCode) {
    navigate(link, link.path);
    return;
  }

  const isGuest = useGuestStore.getState().isGuestMode;
  const { IDtoken, profile, selectedOrg } = bs();

  // Signed out: hold the link. Signing in re-runs this through the subscription below, so the
  // deeplink survives the trip through the sign-in screen instead of being lost at the guard.
  if (!IDtoken && !isGuest) return;

  // A guest session has no organisation membership at all (its selectedOrg is the `__guest__`
  // placeholder), so an org-scoped link is one it cannot open.
  if (isGuest) {
    navigate(link, noAccessPath(link.orgCode));
    return;
  }

  // The membership list is the permission check; without it there is nothing to check against yet.
  if (!profile?.organisations) return;

  if (selectedOrg?.name === link.orgCode) {
    navigate(link, link.path);
    return;
  }

  if (!profile.organisations.some((org) => org.name === link.orgCode)) {
    // The cached profile can be older than an invitation the user has just accepted, so refetch
    // once before telling them they have no access.
    if (!link.profileRefreshed) {
      link.profileRefreshed = true;
      await bs().getProfile();
      void applyPendingDeeplink();
      return;
    }
    navigate(link, noAccessPath(link.orgCode));
    return;
  }

  switching = true;
  try {
    // The default-org preference has to move with the switch: `onReturn` re-applies it about two
    // seconds after every foreground (appEvent.slice.ts), which is the same window a deeplink
    // arrives in, and would otherwise switch the organisation straight back out from under it.
    setUserPref("account", "defaultOrg", link.orgCode);
    // setSelectedOrgById clears the itinerary data and awaits the new org's fetches, holding the
    // overlay loader for the duration, so the item page only mounts once its org is fully loaded.
    await bs().setSelectedOrgById(link.orgCode);
  } catch (error) {
    spErrorTags("Failed to switch organisation for a deeplink", ["DEEPLINK"], error);
  } finally {
    switching = false;
  }

  navigate(link, link.path);
}

// Each gate the resolver waits on lives in the bound store. The selector returns a plain string so
// the subscription fires when one of them actually changes, rather than on every store write.
useBoundStore.subscribe(
  (state) =>
    [
      state.IDtoken ? "1" : "0",
      state.loginCompleted ? "1" : "0",
      state.profile?.organisations?.length ?? -1,
      state.selectedOrg?.name ?? "",
    ].join("|"),
  () => {
    void applyPendingDeeplink();
  },
);
